import { Button } from '@ipfabric/ui-components/Button/Button';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Filter } from '@ipfabric/utils';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useMemo, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import {
  type AutoAssignmentRule,
  useInvalidateRules,
  useRulesApi,
  useRulesQuery,
} from './rulesApi';
import { createRulesTableColumns } from './rulesTableColumns';
import { toRulesTableRow } from './rulesTableRow';

type RulesTableProps = {
  /** Rule handed over from a label source link, shown on its own until cleared. */
  initialRuleId?: string;
};

export const useRulesTable = ({ initialRuleId }: RulesTableProps) => {
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const { showNotification } = useNotifications();
  const [editedRule, setEditedRule] = useState<
    AutoAssignmentRule | 'new' | null
  >(null);

  const rulesQuery = useRulesQuery();
  const invalidateRules = useInvalidateRules();
  const { deleteRule } = useRulesApi();

  const requestDeleteRules = useStableCallback(
    async (ids: string[], name?: string) => {
      const confirmed = await confirmDeleteDialog.show({
        header:
          ids.length === 1 ? 'Rule will be deleted' : 'Rules will be deleted',
        subHeader:
          ids.length === 1
            ? `Do you really want to delete rule "${name ?? ids[0]}"?`
            : `Do you really want to delete ${ids.length} rules?`,
        btnConfirm: 'Delete',
        btnSecondaryConfirm: 'Cancel',
      });
      if (!confirmed) {
        return false;
      }
      await Promise.all(ids.map((id) => deleteRule(id)));
      showNotification({ type: 'success', message: 'Rule(s) deleted' });
      await invalidateRules();
      return true;
    },
  );

  const columns = useMemo(
    () =>
      createRulesTableColumns({
        onEdit: setEditedRule,
        onDelete: (id, description) => {
          void requestDeleteRules([id], description);
        },
      }),
    [],
  );

  const tableRows = useMemo(
    () => (rulesQuery.data ?? []).map(toRulesTableRow),
    [rulesQuery.data],
  );

  const initialFilters = useMemo(
    () =>
      initialRuleId === undefined
        ? undefined
        : { id: ['eq', initialRuleId] satisfies Filter },
    [initialRuleId],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: tableRows,
    initialFilters,
  });

  const bulkActions: TableBulkAction[] = [
    {
      key: 'delete',
      title: 'Delete',
      accent: 'negative',
      disableAutoDeselect: true,
      icon: 'fa-trash-can',
      onClick: async ({
        selectedRows,
        deselectAll,
      }: TableBulkActionClickEvent) => {
        const ids = [...selectedRows].map(String);
        if (await requestDeleteRules(ids)) {
          deselectAll();
        }
      },
    },
  ];

  const menuItems: TableActionsItem[] = [
    {
      standalone: true,
      elementWithKey: (
        <Button
          dataTestId='rules-add-new'
          key='addRule'
          icon='fa-plus-circle'
          onClick={() => setEditedRule('new')}
          size='small'
          type='button'
          variant='outline'
        >
          Add new
        </Button>
      ),
    },
  ];

  return {
    bulkActions,
    columns,
    confirmDeleteDialog,
    editedRule,
    frontendSimpleFilter,
    invalidateRules,
    menuItems,
    rulesQuery,
    setEditedRule,
  };
};
